/**
 * useCalendarGrid Hook
 * Manages calendar grid generation and date calculations
 */
import { useMemo, useCallback } from 'react';
import {
  CalendarRow,
  DayObject,
  MonthData,
  CalendarConfig,
  CalendarMode,
  CustomClassCallback,
  CustomAriaLabelCallback,
  CustomIconClassCallback,
} from '../../../contracts/YearlyCalendarContracts';
import {
  generateCalendarRows,
  generateYearMonths,
  generateYearGrid,
  getYearRange,
  findDayByKey,
  getDateGridPosition,
  getAdjacentDate,
} from '../utils/gridUtils';
import { generateDayHeaders } from '../utils/dateUtils';

/** Stable empty results so skipped computations keep referential identity. */
const EMPTY_YEAR_MONTHS: MonthData[] = [];
const EMPTY_YEAR_GRID: number[][] = [];

export interface UseCalendarGridOptions {
  activeDate: Date;
  selectedDate: Date | null;
  config: CalendarConfig;
  uidPrefix?: string;
  /**
   * Current view mode. Supplying it lets the hook skip the 12 x 42 day-object
   * build that only the yearly views consume - the monthly Resource Dashboard
   * renders three grids (main + two mini-months), so paying for it three times
   * on every navigation is pure waste.
   * Omitted (undefined) keeps the previous always-compute behaviour.
   */
  viewMode?: CalendarMode;
  customClass?: CustomClassCallback;
  customAriaLabel?: CustomAriaLabelCallback;
  customIconClass?: CustomIconClassCallback;
}

export interface UseCalendarGridReturn {  // Generated data
  dayHeaders: string[];
  calendarRows: CalendarRow[];
  yearMonths: MonthData[];
  yearGrid: number[][];
  yearRange: { start: number; end: number };
  
  // Lookup functions
  findDay: (key: string) => DayObject | null;
  getDayPosition: (date: Date) => { rowIndex: number; colIndex: number } | null;
  getNextDate: (date: Date, direction: 'up' | 'down' | 'left' | 'right') => Date | null;
}

/**
 * Custom hook for managing calendar grid state and calculations
 */
export const useCalendarGrid = (options: UseCalendarGridOptions): UseCalendarGridReturn => {
  const {
    activeDate,
    selectedDate,
    config,
    uidPrefix = 'day',
    viewMode,
    customClass,
    customAriaLabel,
    customIconClass,
  } = options;

  // Year-view data is only read by the 'richcc-year' renderer; the year picker
  // grid/range only by the 'year' title and picker.
  const needsYearMonths = viewMode === undefined || viewMode === 'richcc-year';
  const needsYearGrid = viewMode === undefined || viewMode === 'year';
  
  // Generate day headers (Mon, Tue, Wed, etc.)
  const dayHeaders = useMemo(() => {
    return generateDayHeaders(config.startingDay, config.formatDayHeader);
  }, [config.startingDay, config.formatDayHeader]);
  
  // Wrap custom callbacks to match expected signature
  const wrappedCustomClass = useMemo(() => {
    if (!customClass) return undefined;
    return (date: Date) => customClass({ date, mode: 'day' });
  }, [customClass]);

  const wrappedCustomAriaLabel = useMemo(() => {
    if (!customAriaLabel) return undefined;
    return (date: Date) => customAriaLabel({ date, mode: 'day' });
  }, [customAriaLabel]);

  const wrappedCustomIconClass = useMemo(() => {
    if (!customIconClass) return undefined;
    return (date: Date) => customIconClass({ date, mode: 'day' });
  }, [customIconClass]);
  
  // Generate calendar rows for month view
  const calendarRows = useMemo(() => {
    return generateCalendarRows(
      activeDate,
      selectedDate,
      config,
      uidPrefix,
      wrappedCustomClass,
      wrappedCustomAriaLabel,
      wrappedCustomIconClass
    );
  }, [activeDate, selectedDate, config, uidPrefix, wrappedCustomClass, wrappedCustomAriaLabel, wrappedCustomIconClass]);
  
  // Generate year months for yearly view (rolling 12 months starting from activeDate's month)
  const yearMonths = useMemo(() => {
    if (!needsYearMonths) {
      return EMPTY_YEAR_MONTHS;
    }
    return generateYearMonths(activeDate.getFullYear(), selectedDate, config, activeDate.getMonth());
  }, [activeDate, selectedDate, config, needsYearMonths]);
  
  // Generate year picker grid
  const yearGrid = useMemo(() => {
    if (!needsYearGrid) {
      return EMPTY_YEAR_GRID;
    }
    return generateYearGrid(activeDate.getFullYear());
  }, [activeDate, needsYearGrid]);
  
  // Get year range for display
  const yearRange = useMemo(() => {
    return getYearRange(activeDate.getFullYear());
  }, [activeDate]);
  
  // Find a day by its key
  const findDay = useCallback((key: string): DayObject | null => {
    return findDayByKey(calendarRows, key);
  }, [calendarRows]);
  
  // Get grid position for a date
  const getDayPosition = useCallback((date: Date) => {
    return getDateGridPosition(date, calendarRows);
  }, [calendarRows]);
  
  // Get adjacent date for keyboard navigation
  const getNextDate = useCallback((date: Date, direction: 'up' | 'down' | 'left' | 'right') => {
    return getAdjacentDate(date, direction, calendarRows);
  }, [calendarRows]);
  
  return {
    dayHeaders,
    calendarRows,
    yearMonths,
    yearGrid,
    yearRange,
    findDay,
    getDayPosition,
    getNextDate,
  };
};

export default useCalendarGrid;
